import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import {
  adminResolveDispute,
  adminStartBooking,
  adminForceSettle,
  adminForceCancel,
  estimateBalanceDue,
} from '../../services/bookingFlowApi';
import { generateDisputeStatement } from '../../utils/ledger/generateDisputeStatement';

export function AdminDisputePanel({ booking, onDone }) {
  const { t } = useLanguage();
  const [overrideHours, setOverrideHours] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  if (!booking || booking.status !== 'Disputed') return null;

  const submit = async () => {
    const hrs = Number(overrideHours);
    if (!Number.isFinite(hrs) || hrs < Number(booking.minimum_hours || 3)) {
      alert(`Minimum ${booking.minimum_hours || 3} hrs billing required.`);
      return;
    }
    if (!reason.trim()) {
      alert('Reason required');
      return;
    }
    setBusy(true);
    try {
      await adminResolveDispute(booking.id, hrs, reason.trim());
      onDone?.();
    } catch (err) {
      alert(err.message || 'Resolve failed');
    } finally {
      setBusy(false);
    }
  };

  const downloadStatement = () => {
    generateDisputeStatement(booking).catch((err) => {
      console.error('Dispute statement PDF failed:', err);
      alert(err?.message || 'Failed to download dispute statement');
    });
  };

  return (
    <div className="mb-4 rounded-xl border border-red-500/40 bg-red-950/20 p-4">
      <p className="mb-2 text-sm font-bold text-red-400">
        🔴 DISPUTE — Booking #{booking.booking_ref || booking.id}
      </p>
      <p className="mb-1 text-xs text-slate-400">
        Operator logged: {booking.operator_actual_hours ?? '—'} hrs
      </p>
      <p className="mb-3 text-xs text-slate-300">Client says: {booking.dispute_reason || '—'}</p>
      <button
        type="button"
        onClick={downloadStatement}
        className="mb-3 rounded border border-red-400/50 bg-red-950/40 px-3 py-1.5 text-xs font-semibold text-red-200 hover:bg-red-900/50"
      >
        {String.fromCodePoint(0x1F4C4)} {t('downloadDisputeStatement')}
      </button>
      <div className="flex flex-wrap gap-2">
        <input
          type="number"
          placeholder="Override hours"
          value={overrideHours}
          onChange={(e) => setOverrideHours(e.target.value)}
          className="rounded border border-de-gold/30 bg-slate-900 px-2 py-1 text-sm text-white"
        />
        <input
          type="text"
          placeholder="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="min-w-[200px] flex-1 rounded border border-de-gold/30 bg-slate-900 px-2 py-1 text-sm text-white"
        />
        <button
          type="button"
          disabled={busy}
          onClick={submit}
          className="rounded bg-red-600/80 px-3 py-1 text-sm font-semibold text-white"
        >
          Resolve Dispute
        </button>
      </div>
    </div>
  );
}

function isForceSettleCandidate(booking) {
  if (!booking) return false;
  if (booking.client_confirmed_at) return false;
  if (booking.status === 'Completing_PendingPayment') return true;
  if (booking.status !== 'Completing') return false;
  if (booking.client_payment_pending) return true;
  if (!booking.client_confirm_deadline) return false;
  return new Date(booking.client_confirm_deadline) < new Date();
}

export function AdminForceSettlePanel({ booking, clientWalletBalance, onDone }) {
  const [overrideHours, setOverrideHours] = useState('');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');

  if (!isForceSettleCandidate(booking)) return null;

  const due = estimateBalanceDue(booking);
  const wallet = Number(clientWalletBalance ?? 0);
  const deadline = booking.client_confirm_deadline
    ? new Date(booking.client_confirm_deadline).toLocaleString('en-IN')
    : '—';

  const run = async (paymentMethod) => {
    setBusy(paymentMethod);
    setError('');
    try {
      const hrs = overrideHours !== '' ? Number(overrideHours) : undefined;
      if (hrs != null && (!Number.isFinite(hrs) || hrs <= 0)) {
        throw new Error('Override hours must be a positive number');
      }
      await adminForceSettle(booking.id, {
        paymentMethod,
        adminOverrideHours: hrs,
      });
      onDone?.();
    } catch (err) {
      setError(err?.message || 'Force settle failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mb-4 rounded-xl border border-amber-500/40 bg-amber-950/20 p-4">
      <p className="mb-2 text-sm font-bold text-amber-300">
        ⚠️ STUCK COMPLETING — Booking #{booking.booking_ref || booking.id}
      </p>
      <div className="mb-3 grid grid-cols-1 gap-1 text-xs text-slate-300 sm:grid-cols-2">
        <span>Status: {booking.status}</span>
        <span>Operator hrs: {booking.operator_actual_hours ?? '—'}</span>
        <span>Deadline: {deadline} (expired)</span>
        <span className="text-[#C9A84C]">
          Balance due: ₹{due.toLocaleString('en-IN')}
        </span>
        <span>
          Client wallet: ₹{wallet.toLocaleString('en-IN')}
          {wallet < due ? ' (insufficient)' : ''}
        </span>
        {booking.client_payment_pending ? (
          <span className="text-amber-300">client_payment_pending: true</span>
        ) : null}
      </div>
      {error && <p className="mb-2 text-xs text-red-400">{error}</p>}
      <div className="mb-2 flex flex-wrap gap-2">
        <input
          type="number"
          min="0"
          step="0.01"
          placeholder="Override hours (optional)"
          value={overrideHours}
          onChange={(e) => setOverrideHours(e.target.value)}
          className="rounded border border-de-gold/30 bg-slate-900 px-2 py-1 text-sm text-white"
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={Boolean(busy)}
          onClick={() => run('wallet')}
          className="rounded border border-emerald-500/50 bg-emerald-900/40 px-3 py-1.5 text-xs font-semibold text-emerald-200 disabled:opacity-50"
        >
          {busy === 'wallet' ? 'Settling…' : 'Force Settle (wallet)'}
        </button>
        <button
          type="button"
          disabled={Boolean(busy) || booking.status === 'Completing_PendingPayment'}
          onClick={() => run('pending')}
          className="rounded border border-amber-500/50 bg-amber-900/40 px-3 py-1.5 text-xs font-semibold text-amber-200 disabled:opacity-50"
        >
          {busy === 'pending' ? 'Marking…' : 'Mark Payment Pending'}
        </button>
      </div>
    </div>
  );
}

export function AdminForceCancelPanel({ booking, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!booking || booking.status !== 'Active') return null;

  const reasonTrimmed = reason.trim();
  const canConfirm = reasonTrimmed.length >= 20 && !loading;

  const confirm = async () => {
    if (!canConfirm) return;
    setLoading(true);
    setError('');
    try {
      await adminForceCancel(booking.id, reasonTrimmed);
      setOpen(false);
      setReason('');
      onDone?.();
    } catch (err) {
      setError(err?.message || 'Force cancel failed');
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <div className="mb-3">
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{
            background: 'rgba(201,168,76,0.12)',
            border: '1px solid rgba(201,168,76,0.5)',
            borderRadius: '8px',
            color: '#C9A84C',
            fontSize: '13px',
            fontWeight: '600',
            padding: '10px 16px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <AlertTriangle size={16} aria-hidden />
          Force Cancel
          <span style={{ color: '#8896a8', fontWeight: 500, fontSize: '12px' }}>
            · #{booking.booking_ref || booking.id}
          </span>
        </button>
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-xl border border-[#C9A84C]/40 bg-[#030810] p-4">
      <p className="mb-2 text-sm font-bold text-[#C9A84C]">
        Force Cancel — Booking #{booking.booking_ref || booking.id}
      </p>
      <div
        className="mb-3 rounded-lg border border-red-500/40 bg-red-950/30 p-3 text-xs text-red-200"
        role="alert"
      >
        This will cancel the booking and refund the advance to the client wallet. This action cannot be undone.
      </div>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value.slice(0, 500))}
        rows={3}
        maxLength={500}
        placeholder="Enter reason for force cancellation (minimum 20 characters)"
        className="mb-1 w-full rounded-lg border border-[rgba(201,168,76,0.3)] bg-gradient-to-br from-[#0f2040] to-[#0a1628] px-3 py-2 text-sm text-[#e8e0d0] outline-none"
        style={{ boxSizing: 'border-box' }}
      />
      <p className="mb-3 text-xs text-slate-400">
        {reason.length}/500 (minimum 20)
      </p>
      {error ? <p className="mb-2 text-xs text-red-400">{error}</p> : null}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={!canConfirm}
          onClick={confirm}
          className="rounded-lg bg-red-600/90 px-4 py-2 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? 'Cancelling…' : 'Confirm Force Cancel'}
        </button>
        <button
          type="button"
          disabled={loading}
          onClick={() => {
            setOpen(false);
            setError('');
          }}
          className="rounded-lg border border-slate-600/60 bg-transparent px-4 py-2 text-xs font-semibold text-slate-300 hover:bg-slate-800/40 disabled:opacity-50"
        >
          Back
        </button>
      </div>
    </div>
  );
}

export function AdminStartMachineButton({ booking, onDone }) {
  const [busy, setBusy] = useState(false);
  if (!booking) return null;
  if (booking.booking_type === 'contract') return null;
  const canStart = booking.owner_approved
    && (booking.status === 'Active' || booking.status === 'Confirmed')
    && !booking.machine_started_at;
  if (!canStart) return null;

  const start = async () => {
    const ref = booking.booking_ref || booking.id;
    if (!window.confirm(`Start machine for Booking #${ref}? Client timer and operator assignment will begin.`)) {
      return;
    }
    setBusy(true);
    try {
      await adminStartBooking(booking.id);
      onDone?.();
    } catch (err) {
      alert(err.message || 'Start failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      disabled={busy}
      onClick={start}
      className="rounded border border-green-500/60 bg-green-900/40 px-3 py-1.5 text-xs font-bold text-green-300 hover:bg-green-900/60 disabled:opacity-60"
    >
      {busy ? 'Starting…' : `${String.fromCodePoint(0x25B6)} Start Machine`}
    </button>
  );
}
